import { useEffect, useRef } from 'react';
import { Spinner } from '../ui/Spinner';
import type { GenerationEntry } from './useGenerationQueue';

interface ResultRailProps {
  entries: GenerationEntry[];
  /** An entry `key`, not a garment id — see the note on `GenerationEntry.key`. */
  activeKey: string | null;
  onOpen: (key: string) => void;
  onRetry: (key: string) => void;
}

/**
 * The horizontally scrollable rail of try-on results.
 *
 * One card per garment the customer has asked for, in request order. Cards
 * appear the moment a garment is tapped — queued, then generating, then either
 * the finished image or the failure — so a customer who taps five garments sees
 * five cards immediately rather than a blank strip for a minute.
 *
 * A failed card names the garment. Without the name a customer with several
 * generations running cannot tell which one to retry.
 *
 * ⚠️ Cards are identified by `entry.key` (photo + garment), not by garment id.
 * After a retake the same garment legitimately appears twice — once per photo —
 * and keying on the garment would collapse them into one React element with the
 * wrong image.
 */
export function ResultRail({ entries, activeKey, onOpen, onRetry }: ResultRailProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const previousCountRef = useRef(entries.length);

  /**
   * Keeps a newly queued card in view.
   *
   * Without it a customer with a full rail confirms a spend and sees nothing
   * happen: cards are 128px wide with a 12px gap, so on a rail of twenty the
   * twenty-first spinner lands roughly 2,800px off the right-hand edge.
   *
   * ⚠️ Driven by the entry count, not by the Generate button, because `start`
   * does not always append. A failed card is replaced **in place** — deliberately,
   * so it does not jump to the far end — and a tap on a garment already queued or
   * done is ignored outright. Only a real append moves the count, and only a real
   * append needs the rail moved.
   */
  useEffect(() => {
    const previous = previousCountRef.current;
    previousCountRef.current = entries.length;

    /**
     * Growing *from empty* is never a new card worth chasing. It is either the
     * rehydrate from IndexedDB after a reload or an iOS cold start — where the
     * customer should see the visit from its beginning rather than be thrown to
     * the end — or the first generation of a visit, where there is nothing to
     * scroll yet.
     *
     * A rehydrate that lands *after* a first tap still scrolls, and correctly so:
     * restored rows are prepended, so the end of the rail is the card the
     * customer just asked for.
     */
    if (previous === 0 || entries.length <= previous) return;

    const scroller = scrollerRef.current;
    if (!scroller) return;

    /**
     * `base.css` neutralises transitions under `prefers-reduced-motion`, but that
     * rule cannot reach a programmatic scroll, so it is checked by hand. Smooth
     * over ~2,800px is a long animation to inflict on someone who asked for less
     * of it.
     */
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scroller.scrollTo({ left: scroller.scrollWidth, behavior: reduced ? 'auto' : 'smooth' });
  }, [entries.length]);

  // Below the hooks, not above: an empty rail renders nothing, but the effect
  // still has to run so the count is tracked from the start of the visit.
  if (entries.length === 0) return null;

  return (
    <div
      ref={scrollerRef}
      className="rail flex gap-3 border-t border-(--color-border-subtle) bg-surface px-4 py-3"
    >
      {entries.map((entry) => (
        <ResultCard
          key={entry.key}
          entry={entry}
          active={entry.key === activeKey}
          onOpen={() => onOpen(entry.key)}
          onRetry={() => onRetry(entry.key)}
        />
      ))}
    </div>
  );
}

interface ResultCardProps {
  entry: GenerationEntry;
  active: boolean;
  onOpen: () => void;
  onRetry: () => void;
}

function ResultCard({ entry, active, onOpen, onRetry }: ResultCardProps) {
  const border = active
    ? 'border-(--color-border-accent)'
    : 'border-(--color-border-subtle)';

  return (
    <div
      className={`flex w-32 shrink-0 flex-col overflow-hidden rounded-lg border-2 bg-surface ${border}`}
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={entry.status !== 'done'}
        /**
         * ⚠️ **`aspect-2/3` matches FASHN's output exactly (864×1296).** At the
         * old `aspect-3/4` the box was wider than the image, so `object-cover`
         * cropped ~11% off the top and bottom — heads and feet, which on a
         * full-body try-on is the part the customer is looking for. Cards were
         * always the same size; it was the *content* that varied, differently per
         * photo depending on how the subject was framed.
         *
         * ⚠️ **Coupled to the model's output ratio.** A move to `tryon-max`, or a
         * different `resolution`, changes it — at which point this becomes a crop
         * again rather than a fit. If that ratio ever stops being fixed, swap the
         * `object-cover` below for `object-contain` and accept letterboxing.
         */
        className="relative flex aspect-2/3 w-full items-center justify-center bg-subtle"
      >
        {entry.status === 'done' && entry.imageUrl ? (
          <img
            src={entry.imageUrl}
            alt={`You wearing ${entry.garmentName}`}
            className="h-full w-full object-cover"
          />
        ) : entry.status === 'failed' ? (
          <span className="px-2 text-center text-xs text-danger-text">
            {entry.failure?.message ?? 'Something went wrong.'}
          </span>
        ) : (
          <div className="flex flex-col items-center gap-2 text-tertiary">
            <Spinner size={20} label={entry.status === 'queued' ? 'Waiting' : 'Generating'} />
            <span className="text-xs">{entry.status === 'queued' ? 'Waiting' : 'Working…'}</span>
          </div>
        )}
      </button>

      <div className="flex flex-col gap-1 p-2">
        <p className="line-clamp-1 text-xs font-medium text-primary">{entry.garmentName}</p>
        {entry.status === 'failed' && entry.failure?.retryable && (
          <button
            type="button"
            onClick={onRetry}
            className="text-xs font-semibold text-accent-text underline"
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
}
